/**
 * IndustryProfilePanel - Displays the Industry Viability Profile (IVP)
 *
 * Shows 7 IVP sections in collapsible panels:
 * - Vital Signs with normative ranges
 * - Value Chain critical path
 * - Common Pathologies with severity
 * - Revenue Model, Cost Structure, Growth Levers, Environmental Sensitivities
 */

import { useEffect, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@state/hooks'
import { fetchIVP, generateIVP, toggleSection, clearIVP } from '@state/slices/ivpSlice'
import type { IVPVitalSign, IVPPathology, IVPValueChainStep } from '@services/companyProfileApi'
import './IndustryProfilePanel.css'

interface IndustryProfilePanelProps {
  companyId: number
}

export function IndustryProfilePanel({ companyId }: IndustryProfilePanelProps) {
  const dispatch = useAppDispatch()
  const { ivp, loading, generating, error, expandedSections } = useAppSelector(state => state.ivp)

  useEffect(() => {
    if (companyId) {
      dispatch(fetchIVP(companyId))
    }
    return () => { dispatch(clearIVP()) }
  }, [companyId, dispatch])

  const handleGenerate = useCallback(() => {
    dispatch(generateIVP({ companyId }))
  }, [companyId, dispatch])

  const handleToggle = useCallback((section: string) => {
    dispatch(toggleSection(section))
  }, [dispatch])

  if (loading) {
    return <div className="ivp-panel ivp-loading">Loading industry profile...</div>
  }

  if (!ivp) {
    return (
      <div className="ivp-panel ivp-empty">
        <p>No Industry Viability Profile available.</p>
        <button
          className="ivp-generate-btn"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating Profile...' : 'Generate Industry Profile'}
        </button>
        {error && <p className="ivp-error">{error}</p>}
      </div>
    )
  }

  return (
    <div className="ivp-panel">
      <div className="ivp-header">
        <h4>{ivp.industry_label}</h4>
        <span className="ivp-meta">
          NAICS {ivp.naics_code} | {ivp.revenue_tier}
        </span>
      </div>

      {/* Vital Signs */}
      <CollapsibleSection
        title="Vital Signs"
        badge={ivp.vital_signs?.metrics?.length || 0}
        expanded={expandedSections.vital_signs !== false}
        onToggle={() => handleToggle('vital_signs')}
      >
        <div className="ivp-vital-signs">
          {ivp.vital_signs?.metrics?.map((metric, i) => (
            <VitalSignCard key={i} metric={metric} />
          ))}
        </div>
      </CollapsibleSection>

      {/* Value Chain */}
      <CollapsibleSection
        title="Value Chain"
        expanded={expandedSections.value_chain !== false}
        onToggle={() => handleToggle('value_chain')}
      >
        <div className="ivp-value-chain">
          {ivp.value_chain?.description && (
            <p className="ivp-description">{ivp.value_chain.description}</p>
          )}
          <div className="ivp-chain-path">
            {ivp.value_chain?.critical_path?.map((step, i) => (
              <ChainStep key={i} step={step} isLast={i === ivp.value_chain.critical_path.length - 1} />
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Common Pathologies */}
      <CollapsibleSection
        title="Common Pathologies"
        badge={ivp.common_pathologies?.pathologies?.length || 0}
        expanded={expandedSections.common_pathologies !== false}
        onToggle={() => handleToggle('common_pathologies')}
      >
        <div className="ivp-pathologies">
          {ivp.common_pathologies?.pathologies?.map((path, i) => (
            <PathologyCard key={i} pathology={path} />
          ))}
        </div>
      </CollapsibleSection>

      {/* Revenue Model */}
      <CollapsibleSection
        title="Revenue Model"
        expanded={expandedSections.revenue_model === true}
        onToggle={() => handleToggle('revenue_model')}
      >
        <JsonSection data={ivp.revenue_model} />
      </CollapsibleSection>

      {/* Cost Structure */}
      <CollapsibleSection
        title="Cost Structure"
        expanded={expandedSections.cost_structure === true}
        onToggle={() => handleToggle('cost_structure')}
      >
        <JsonSection data={ivp.cost_structure} />
      </CollapsibleSection>

      {/* Growth Levers */}
      <CollapsibleSection
        title="Growth Levers"
        expanded={expandedSections.growth_levers === true}
        onToggle={() => handleToggle('growth_levers')}
      >
        <JsonSection data={ivp.growth_levers} />
      </CollapsibleSection>

      {/* Environmental Sensitivities */}
      <CollapsibleSection
        title="Environmental Sensitivities"
        expanded={expandedSections.env_sensitivities === true}
        onToggle={() => handleToggle('env_sensitivities')}
      >
        <JsonSection data={ivp.env_sensitivities} />
      </CollapsibleSection>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function CollapsibleSection({ title, badge, expanded, onToggle, children }: {
  title: string
  badge?: number
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className={`ivp-section ${expanded ? 'expanded' : 'collapsed'}`}>
      <div
        className="ivp-section-header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
        aria-expanded={expanded}
      >
        <span className="ivp-collapse-icon">{expanded ? '\u25BC' : '\u25B6'}</span>
        <span className="ivp-section-title">{title}</span>
        {badge !== undefined && badge > 0 && (
          <span className="ivp-badge">{badge}</span>
        )}
      </div>
      {expanded && <div className="ivp-section-content">{children}</div>}
    </div>
  )
}

function VitalSignCard({ metric }: { metric: IVPVitalSign }) {
  const nr = metric.normative_range
  const importanceClass = `ivp-importance-${metric.importance}`

  return (
    <div className={`ivp-vital-card ${importanceClass}`}>
      <div className="ivp-vital-name">{metric.name}</div>
      <div className="ivp-vital-range">
        <span className="ivp-range-label">p25</span>
        <span className="ivp-range-value">{formatValue(nr.p25, metric.unit)}</span>
        <span className="ivp-range-label">median</span>
        <span className="ivp-range-value ivp-range-median">{formatValue(nr.median, metric.unit)}</span>
        <span className="ivp-range-label">p75</span>
        <span className="ivp-range-value">{formatValue(nr.p75, metric.unit)}</span>
      </div>
      {metric.actual_value && (
        <div className="ivp-vital-actual">
          Actual: <strong>{metric.actual_value}</strong>
        </div>
      )}
      <div className="ivp-vital-source">{metric.source}</div>
    </div>
  )
}

function ChainStep({ step, isLast }: { step: IVPValueChainStep; isLast: boolean }) {
  const perspectiveColors: Record<string, string> = {
    'Learning_Growth': '#6366f1',
    'Internal_Process': '#f59e0b',
    'Customer': '#10b981',
    'Financial': '#ef4444',
  }
  const color = perspectiveColors[step.perspective] || '#6b7280'

  return (
    <div className="ivp-chain-step">
      <div className="ivp-chain-node" style={{ borderColor: color }}>
        <span className="ivp-chain-perspective" style={{ color }}>
          {step.perspective.replace('_', ' ')}
        </span>
        <span className="ivp-chain-name">{step.node.replace(/_id$/, '').replace(/_/g, ' ')}</span>
        {step.role && <span className="ivp-chain-role">{step.role}</span>}
      </div>
      {!isLast && <span className="ivp-chain-arrow">&darr;</span>}
    </div>
  )
}

function PathologyCard({ pathology }: { pathology: IVPPathology }) {
  const severityClass = `ivp-severity-${pathology.severity}`

  return (
    <div className={`ivp-pathology-card ${severityClass}`}>
      <div className="ivp-pathology-header">
        <span className="ivp-pathology-name">{pathology.name}</span>
        <span className={`ivp-pathology-badge ${severityClass}`}>{pathology.severity}</span>
      </div>
      <p className="ivp-pathology-desc">{pathology.description}</p>
      {pathology.ontology_cluster.length > 0 && (
        <div className="ivp-pathology-nodes">
          {pathology.ontology_cluster.map(n => (
            <span key={n} className="ivp-node-tag">{n.replace(/_id$/, '').replace(/_/g, ' ')}</span>
          ))}
        </div>
      )}
    </div>
  )
}

function JsonSection({ data }: { data: Record<string, any> }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="ivp-empty-section">No data available</p>
  }

  return (
    <div className="ivp-json-section">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="ivp-json-entry">
          <span className="ivp-json-key">{key.replace(/_/g, ' ')}</span>
          <span className="ivp-json-value">
            {typeof value === 'string' ? value :
             Array.isArray(value) ? value.map((v, i) => (
               <div key={i} className="ivp-json-item">
                 {typeof v === 'object' ? (v.name || v.description || JSON.stringify(v)) : String(v)}
               </div>
             )) :
             typeof value === 'object' ? JSON.stringify(value, null, 2) :
             String(value)}
          </span>
        </div>
      ))}
    </div>
  )
}

// Formatting helper
function formatValue(val: number, unit: string): string {
  if (unit === 'percentage') return `${(val * 100).toFixed(0)}%`
  if (unit === 'currency') return `$${val.toLocaleString()}`
  if (unit === 'days') return `${val}d`
  return String(val)
}

export default IndustryProfilePanel
